import * as d3 from 'd3-ease';
import React, { useEffect, useRef } from 'react';

interface TickToProps {
  value: number;
  ttl?: number;
  formatter: (value: number) => string;
}

export const TickTo: React.FC<TickToProps> = ({
  value,
  ttl = 100,
  formatter,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!spanRef.current) {
      return;
    }

    const startValue = parseFloat(
      spanRef.current.getAttribute('data-last-value') ?? '0',
    );
    const startTS = Date.now();
    let currentFrame: number | undefined = undefined;
    const delta = value - startValue;
    if (value === startValue) {
      spanRef.current.innerText = formatter(value);
      return;
    }

    const runUntilDone = (skip: boolean) => {
      currentFrame = requestAnimationFrame(() => {
        // clamp the value from 0 -> 1
        const gradient = d3.easeCircleIn(
          Math.min(1, Math.max(0, (Date.now() - startTS) / ttl)),
        );

        // Animating on 2's so skip every other frame
        if (!skip && spanRef.current) {
          if (startValue < value) {
            spanRef.current.innerText = formatter(
              startValue + delta * gradient,
            );
          }
          if (startValue > value) {
            spanRef.current.innerText = formatter(
              startValue + delta * gradient,
            );
          }
        }

        if (gradient <= 1) {
          if (spanRef.current) {
            spanRef.current.setAttribute('data-last-value', `${value}`);
          }
          runUntilDone(!skip);
        }
      });
    };

    runUntilDone(false);

    return () => {
      if (currentFrame !== undefined) {
        cancelAnimationFrame(currentFrame);
      }
    };
  }, [formatter, ttl, value]);

  return <span ref={spanRef} />;
};
